import { InputFieldConfig } from '../components/addressSchema/addressSchemaManager';
import { AddressStruct, CheckDistancePoi } from '../types';

/**
* GCC国家，根据schema下发反写逻辑，正则匹配填写
*/
function getFormatAddress(template: string, content: any) {
  // 替换占位符并处理没有值的情况
  let formattedStr = template.replace(/{([^}]+)}/g, (_, key) => {
    const keys = key.split('/');

    // 找到第一个可用值
    let value = '';
    const foundKey = keys?.find((itemKey) => content?.[itemKey]);
    if (foundKey) {
      value = content[foundKey];
    }

    return value || ''; // 返回空字符串以表示这个字段没值
  });
  // 使用正则表达式清理多余的逗号和空格
  formattedStr = formattedStr.replace(/,(\s*,+)+/g, ',').replace(/,\s*$/, '').replace(/^\s*,/, '').trim();

  return formattedStr;
}

/*
* 根据schema拼接规则，获取LBS默认信息
*/
export function getAddressFromSchemaLBS(addressSchemaInfo, addressStruct) {
  const addressInfoFromLbs = {};

  const lbsInfoHandler = (configs: InputFieldConfig[]) => {
    configs.forEach((config) => {
      if (config.poiRelation) {
        // lbs拼接地址超长兜底
        let poiRelationStr = getFormatAddress(config.poiRelation, addressStruct);
        if (poiRelationStr.length > config.maxLength) {
          poiRelationStr = poiRelationStr.substring(0, config.maxLength);
        }
        addressInfoFromLbs[config.param] = poiRelationStr;
      }
    });
  };

  if (addressSchemaInfo) {
    //上方输入框默认值，获取lbs默认信息
    addressSchemaInfo.byRegionCommand?.fields?.forEach((configs) => {
      lbsInfoHandler(configs);
    });

    //下方地址标签输入框，获取lbs默认信息
    addressSchemaInfo?.addressType?.forEach((item) => {
      const key = Object.keys(item)[0];
      const fields = item[key]?.fields;

      fields?.forEach((configs) => {
        lbsInfoHandler(configs);
      });
    });
  }

  return addressInfoFromLbs;
}

/**
 * 拼接结构化信息进行展示
 * 展示规则
 * 英文：大厦名称+英文逗号+街道号+空格+街道名+英文逗号+行政区划+英文逗号+城市
 * @param addressStruct 结构化信息
 * @param isFilterPremise 是否有大厦名称
 * @returns
 */
function joinAddressStruct(addressStruct?: AddressStruct | CheckDistancePoi, isFilterPremise = false) {
  if (!addressStruct) return '';
  const {
    province = '', subDistrict = '', street = '', streetNumber = '', premise = ''
  } = addressStruct;
  const streetInfo = [streetNumber, street].filter((item) => item).join(' ');
  if (isFilterPremise) {
    return [streetInfo, subDistrict, province].filter((item) => item).join(',');
  }
  return [premise, streetInfo, subDistrict, province].filter((item) => item).join(',');
}

/**
 * 返回结构化地址或者非结构化地址
 * @param addressStruct 结构化信息
 * @param isFilterPremise 是否有大厦名称
 * @param isHKStructRegion 是否为香港结构化地址
 * @returns
 */
function getAddress(addressStruct?: AddressStruct | CheckDistancePoi, isFilterPremise = false, isHKStructRegion: boolean = true) {
  if (!isHKStructRegion) {
    // @ts-ignore
    return addressStruct?.address ?? '';
  }
  return joinAddressStruct(addressStruct, isFilterPremise);
}

/**
 * 获取开放地图地址信息 https://km.sankuai.com/collabpage/2743558515
 */
export function getAddressInOpenMap(addressStruct?: AddressStruct) {
  if (!addressStruct) return '';
  const {
    district = '', subDistrict, city = '', country = ''
  } = addressStruct;
  if (subDistrict) {
    return [subDistrict, city, country].filter((item) => item).join(',');
  }
  return [district, city, country].filter((item) => item).join(',');
}

/**
* GCC国家，根据schemaInfo和addressInfo获取显示的地址
*/
export function getDisplayFormatAddress(addressSchemaInfo, addressInfo) {
  if (!addressSchemaInfo || !addressInfo) {
    return '';
  }
  const addressInfoFromLbs = getAddressFromSchemaLBS(addressSchemaInfo, addressInfo);
  return getFormatAddress(addressSchemaInfo.combinedAddressBriefTemplate, { ...addressInfo, ...addressInfoFromLbs });
}

export function getListFormatAddress(addressSchemaInfo, addressInfo) {
  if (!addressSchemaInfo || !addressInfo) {
    return '';
  }
  const addressInfoFromLbs = getAddressFromSchemaLBS(addressSchemaInfo, addressInfo);
  if (addressSchemaInfo.pinPageListTemplate) {
    return getFormatAddress(addressSchemaInfo.pinPageListTemplate, { ...addressInfo, ...addressInfoFromLbs });
  }
  return getFormatAddress(addressSchemaInfo.combinedAddressBriefTemplate, { ...addressInfo, ...addressInfoFromLbs });
}

/**
* GCC国家，根据schemaInfo和addressInfo获取显示的地址
* 未配置结构化时，返回之前的展示方式
*/
export function getDisplayAddress(
  addressSchemaInfo,
  addressInfo,
  isFilterPremise = false,
  isHKStructRegion: boolean = true,
  template: string | null = null
) {
  // 展示 poi 气泡信息数据
  if (!template) {
    template = addressSchemaInfo?.combinedAddressBriefTemplate;
  }

  if (addressSchemaInfo && template) {
    const addressInfoFromLbs = getAddressFromSchemaLBS(addressSchemaInfo, addressInfo);
    return getFormatAddress(template, { ...addressInfo, ...addressInfoFromLbs });
  }
  return getAddress(addressInfo, isFilterPremise, isHKStructRegion);
}

export { getFormatAddress, joinAddressStruct, getAddress };
